"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { Link2, ListOrdered, Mail } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/ai-agents/inbox-apply";
const APP = "/autoapply";

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "recruiter",
    name: "Recruiter Finder",
    title: "Find the Person Who Hires",
    description:
      "After you apply, Inbox Apply finds the recruiter or hiring manager for that role and shows you who they are before any message goes out.",
    imageSrcUrl: `${IMG}/hero-recruiter.png`,
    primaryActionText: "Start Inbox Apply",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "note",
    name: "Personal Note",
    title: "A Note They Will Actually Read",
    description:
      "Each message names the role, gives one result from your resume that fits the job and asks for one small thing, all under LinkedIn's 300-character limit.",
    imageSrcUrl: `${IMG}/hero-note.png`,
    primaryActionText: "Start Inbox Apply",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "replies",
    name: "Reply Tracking",
    title: "See Who Accepted and Who Replied",
    description:
      "Track every message from sent to accepted, opened and replied, and see new replies the moment they arrive.",
    imageSrcUrl: `${IMG}/hero-replies.png`,
    primaryActionText: "Start Inbox Apply",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
];

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "paired-apply",
    title: "Paired With Every Application",
    subtitle: "ONE CLICK, TWO MOVES",
    description:
      "Turn on recruiter messaging when you apply through Live Apply or Auto Apply. Each application is followed by a note to the hiring team for that job.",
    benefits: [
      "Toggle per job or for all",
      "Works with Live Apply",
      "Works with Auto Apply",
      "Status shown next to each job",
    ],
    primaryCTA: "Pair My Applications",
    primaryLink: APP,
    secondaryCTA: "Explore Live Apply",
    secondaryLink: "/ai-agents/live-apply",
    imageSrcUrl: `${IMG}/paired-apply.png`,
    icon: <Link2 className="w-4 h-4" />,
    stats: "Apply + message",
  },
  {
    id: "dm-queue",
    title: "Sent Through the Day, Not All at Once",
    subtitle: "PERSONAL AND SAFE",
    description:
      "Messages wait in an outreach queue and go out spaced through the day, so your outreach stays personal and your LinkedIn account stays protected.",
    benefits: [
      "Outreach queue you can review",
      "Paced sending",
      "Edit before it sends",
      "Daily sent and accepted counts",
    ],
    primaryCTA: "Start Inbox Apply",
    primaryLink: APP,
    imageSrcUrl: `${IMG}/dm-queue.png`,
    icon: <ListOrdered className="w-4 h-4" />,
    stats: "Paced sending",
    orientation: "reversed",
  },
];

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "hm-email",
    title: "Email the Hiring Manager",
    subtitle: "STRAIGHT TO THEIR INBOX",
    description:
      "Where a verified work email is available, Inbox Apply sends a short email with your tailored resume attached and tells you when it is opened.",
    benefits: [
      "Verified work emails",
      "Tailored resume attached",
      "Open tracking",
      "Written from your resume and the job",
    ],
    primaryCTA: "Start Inbox Apply",
    primaryLink: APP,
    secondaryCTA: "Write Outreach Manually",
    secondaryLink: "/features/insider-connections",
    imageSrcUrl: `${IMG}/hm-email.png`,
    icon: <Mail className="w-4 h-4" />,
    stats: "Email + LinkedIn",
  },
];

// Replace with real user quotes before publishing.
const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "I applied and the recruiter got a note from me the same hour. She replied that afternoon and moved me to the hiring manager.",
    name: "Aisha K.",
    role: "Data Scientist",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "The notes are short and specific, never 'please consider me'. Nine of my fourteen connection requests were accepted.",
    name: "Marcus T.",
    role: "DevOps Engineer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "Seeing that the hiring manager opened my email twice told me to follow up. That follow-up became my first interview.",
    name: "Emily G.",
    role: "Marketing Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=800&fit=crop&q=80",
  },
];

export default function InboxApplyAgentPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Apply Straight to",
            titleHighlight: "Their Inbox.",
            subtitle:
              "An application alone can sit unread for weeks. Inbox Apply finds the recruiter for each job, sends a short personal note with your best result and shows you who replied.",
            ctaText: "Start Inbox Apply",
            ctaLink: APP,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Get Seen, Not Just Submitted.",
            sectionSubtitle:
              "Every application paired with a message to someone who can move it forward.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Replies From the Hiring Team",
            subtitle: "From job seekers who stopped waiting on the portal",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About Inbox Apply",
            subtitle: "Who gets messaged, what is sent and how it is tracked.",
            faqs,
            ctaTitle: "Ready to reach the hiring team?",
            ctaText: "Start Inbox Apply",
            ctaLink: APP,
          },
        },
      ]}
    />
  );
}
